/**
 * Nana Wisdom Popup
 *
 * Displays random grandmother wisdom as a delightful popup.
 * Appears based on probability and cooldown rules from GuideContext.
 *
 * Australian English throughout.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Sparkles, Quote } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';

/**
 * Category icons and colours
 */
const CATEGORY_CONFIG = {
  digital: {
    icon: '📱',
    colour: '#8b5cf6',
    label: 'Digital Wisdom'
  },
  conflict: {
    icon: '🤝',
    colour: '#f59e0b',
    label: 'Conflict Resolution'
  },
  growing_apart: {
    icon: '🌱',
    colour: '#10b981',
    label: 'Life Changes'
  },
  boundaries: {
    icon: '🛡️',
    colour: '#ec4899',
    label: 'Boundaries'
  },
  timeless: {
    icon: '💫',
    colour: '#6366f1',
    label: 'Timeless Truths'
  },
  encouragement: {
    icon: '💪',
    colour: '#ef4444',
    label: 'Encouragement'
  }
};

/**
 * Main Nana Wisdom Popup Component
 */
const NanaWisdomPopup = () => {
  const {
    pendingNanaWisdom,
    nanaVariant,
    markNanaWisdomSeen
  } = useGuide();

  const [isVisible, setIsVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Show popup when wisdom is pending
  useEffect(() => {
    if (pendingNanaWisdom && !hasInteracted) {
      // Small delay for dramatic effect
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pendingNanaWisdom, hasInteracted]);

  const handleDismiss = () => {
    setIsVisible(false);
    setHasInteracted(true);

    if (pendingNanaWisdom) {
      markNanaWisdomSeen(pendingNanaWisdom.id);
    }
  };

  const handleLove = () => {
    // Could track "loved" wisdom for future feature
    handleDismiss();
  };

  if (!pendingNanaWisdom || !isVisible) return null;

  const categoryConfig = CATEGORY_CONFIG[pendingNanaWisdom.category] || CATEGORY_CONFIG.timeless;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={handleDismiss}
        />

        {/* Popup Card */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-sm bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Decorative top accent */}
          <div
            className="h-2"
            style={{ backgroundColor: categoryConfig.colour }}
          />

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-6">
            {/* Nana avatar and intro */}
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                className="w-14 h-14 rounded-full bg-amber-200 flex items-center justify-center text-2xl border-3 border-amber-300"
                animate={{ rotate: [-2, 2, -2] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                👵
              </motion.div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  {nanaVariant?.name || 'Nana'}
                </h3>
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {nanaVariant?.tagline || 'A bit of wisdom for you'}
                </p>
              </div>
            </div>

            {/* Category badge */}
            <div className="mb-4">
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: categoryConfig.colour }}
              >
                <span>{categoryConfig.icon}</span>
                {categoryConfig.label}
              </span>
            </div>

            {/* Wisdom quote */}
            <div className="relative bg-white rounded-2xl p-5 shadow-inner border border-amber-100">
              <Quote className="absolute top-3 left-3 w-6 h-6 text-amber-200" />
              <p className="text-slate-700 leading-relaxed pl-6 text-base">
                {pendingNanaWisdom.text}
              </p>
            </div>

            {/* Mood indicator */}
            {pendingNanaWisdom.mood && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-4 text-xs text-amber-600 text-center capitalize"
              >
                A {pendingNanaWisdom.mood} moment
              </motion.p>
            )}

            {/* Action buttons */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleLove}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors"
              >
                <Heart className="w-4 h-4" />
                Thanks, Nana!
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-3 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Later
              </button>
            </div>
          </div>

          {/* Decorative bottom pattern */}
          <div className="h-3 bg-gradient-to-r from-amber-200 via-orange-200 to-amber-200 opacity-50" />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NanaWisdomPopup;
